import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray } from '@/types/shared';

/**
 * Customer services tab — `GET /customers/:id/services` (§; backend
 * `GetServicesByCustomerIdRow`). Split into Official vs POC via `is_poc`, which
 * the backend binds as a RAW `is_poc` query param (`form:"is_poc"`), not inside
 * the base64 `filters` blob — so it is sent raw here. `service_entity_name` maps
 * to the backend `name` (service-instance name) column.
 */

export interface ServiceRow {
  service_id: number;
  service_name?: string;
  product_group_name?: string;
  service_entity_name?: string;
  service_status?: string;
  service_price_register?: number;
  service_price_recurring?: number;
  service_start_date?: string;
  service_stopped_date?: string;
  configoptions?: unknown[];
}

export interface ServicesParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
}

function servicesQuery(is_poc: boolean, params: ServicesParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number | boolean> = { is_poc };
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/**
 * `GET /customers/:id/services` — Official (`is_poc:false`) or POC
 * (`is_poc:true`) service list for one customer.
 */
export function useCustomerServices(
  id: number,
  { is_poc }: { is_poc: boolean },
  params: ServicesParams = {},
) {
  return useQuery({
    queryKey: ['customers', 'services', id, is_poc, params],
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow>>(
        `/customers/${id}/services?${servicesQuery(is_poc, { page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}
